// frontend/src/modules/payroll/pages/RunDetailPage.tsx
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Send, Calculator, ChevronDown, FileDown, Landmark, Ban, PlugZap, BadgeCheck, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Badge } from '../../../components/ui/badge';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { payrollApi } from '../api';
import { runStatusView, isRunCommitted, isRunPaid } from '../runStatus';
import { quickbooksApi } from '../../integrations/api';
import { useQuickBooksSync } from '../../integrations/useQuickBooksSync';
import { confirmRePush, reportPushResult } from '../../integrations/pushFeedback';
import { AddManualComponentDialog } from '../components/AddManualComponentDialog';
import type { PayrollRun, PayrollComponent, Payslip } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

export function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  const confirm = useConfirm();

  const runQ      = useApi<{ run: PayrollRun }>(id ? `/api/payroll/runs/${id}` : null);
  const compQ     = useApi<{ rows: PayrollComponent[] }>(id ? `/api/payroll/runs/${id}/components` : null);
  const slipsQ    = useApi<{ payslips: Payslip[] }>(id ? `/api/payroll/runs/${id}/payslips` : null);
  const empQ      = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const qbo = useQuickBooksSync('payroll_run', id ? [id] : []);
  const qboPushed = id ? qbo.isPushed(id) : false;
  const [addOpen, setAddOpen] = React.useState(false);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);
  const [achExporting, setAchExporting] = React.useState(false);
  const [journalExporting, setJournalExporting] = React.useState(false);
  const [qboPushing, setQboPushing] = React.useState(false);

  async function exportAch(runId: string, periodStart: string) {
    setAchExporting(true);
    try {
      // Dry-run first: surface employees that will be EXCLUDED (missing or
      // invalid bank details) before the file downloads.
      const preview = await payrollApi.achPreview(runId);
      if (preview.included === 0) {
        toast.error('No eligible employees', {
          description: 'No payslip on this run has complete bank details (account, routing, type). Add them on each employee\'s HR profile.',
        });
        return;
      }
      if (preview.excluded.length > 0) {
        const names = preview.excluded.map((x) => `• ${x.name} (${x.reason.replace(/_/g, ' ')})`).join('\n');
        const ok = await confirm({
          title: `Continue with the remaining ${preview.included}?`,
          description: (
            <span className="whitespace-pre-line">
              {`${preview.excluded.length} employee(s) will be EXCLUDED from the ACH file:\n\n${names}`}
            </span>
          ),
          confirmLabel: 'Continue',
        });
        if (!ok) return;
      }
      await payrollApi.downloadAch(runId, periodStart);
      toast.success('ACH file downloaded', {
        description: `${preview.included} entr${preview.included === 1 ? 'y' : 'ies'} · effective ${preview.effective_date}`,
      });
    } catch (err: any) {
      toast.error('ACH export failed', { description: err.message });
    } finally { setAchExporting(false); }
  }

  const run = runQ.data?.run;
  const components = compQ.data?.rows ?? [];
  const payslips = slipsQ.data?.payslips ?? [];
  const employeesById: Record<string, EmployeeWithUser> = {};
  for (const e of empQ.data?.employees ?? []) employeesById[e.id] = e;

  function reloadAll() { compQ.reload(); slipsQ.reload(); runQ.reload(); }

  async function runAction(label: string, fn: () => Promise<unknown>, confirmMessage?: string) {
    if (confirmMessage && !(await confirm({ title: confirmMessage }))) return;
    setBusy(label);
    try { await fn(); toast.success(`${label} done`); reloadAll(); }
    catch (err: any) { toast.error(`${label} failed`, { description: err.message }); }
    finally { setBusy(null); }
  }

  async function cancelRun(runId: string) {
    const ok = await confirm({
      title: 'Cancel this payroll run?',
      description: 'The run is kept for the record but marked cancelled — its payslips won\'t be distributed. This can\'t be undone.',
      destructive: true,
      confirmLabel: 'Cancel run',
      cancelLabel: 'Keep run',
    });
    if (!ok) return;
    setBusy('Cancel');
    try {
      await payrollApi.cancelRun(runId);
      toast.success('Run cancelled');
      reloadAll();
    } catch (err: any) {
      toast.error('Cancel failed', { description: err.message });
    } finally { setBusy(null); }
  }

  async function markPaid(runId: string) {
    const ok = await confirm({
      title: 'Mark this run as paid?',
      description:
        'Only do this once the money has actually left your account. A paid run is locked for good — it can no longer be reverted to draft, cancelled, or edited, and its components are frozen.',
      confirmLabel: 'Yes, mark as paid',
      cancelLabel: 'Not yet',
    });
    if (!ok) return;
    setBusy('Mark as paid');
    try {
      await payrollApi.markRunPaid(runId);
      toast.success('Run marked as paid', { description: 'This run is now locked.' });
      reloadAll();
    } catch (err: any) {
      toast.error('Could not mark as paid', { description: err.message });
    } finally { setBusy(null); }
  }

  if (runQ.loading || !run) return <LoadingState variant="page" />;

  const isDraft = run.status === 'draft';
  const isPaid = isRunPaid(run);
  // Exports/pushes stay available once the math is locked — finalized OR paid.
  const isCommitted = isRunCommitted(run);
  // Distribute is allowed for any COMMITTED run (finalized OR paid). Gating it
  // on 'finalized' alone meant that marking a run paid — the natural order,
  // money moves then you confirm — permanently hid Distribute, and since 'paid'
  // is terminal there was no way back: payslips could never be emailed for that
  // run. The backend explicitly permits it (distribute.service's
  // isCommittedRunStatus).
  const canDistribute = isCommitted && !run.distributed_at;
  // Revert, by contrast, must stay blocked once paid — a paid run is frozen
  // (runs.service refuses revertToDraft/cancelRun with `run_paid`).
  const canRevert = run.status === 'finalized' && !run.distributed_at;
  // Matches the backend transition guard: any undistributed, unpaid run can be
  // cancelled. A paid run is frozen.
  const isCancellable = run.status !== 'cancelled' && !run.distributed_at && !isPaid;

  // Group components by employee_id.
  const byEmp: Record<string, PayrollComponent[]> = {};
  for (const c of components) (byEmp[c.employee_id] ||= []).push(c);
  const slipsByEmp: Record<string, Payslip> = {};
  for (const s of payslips) slipsByEmp[s.employee_id] = s;

  // Status chip
  const { tone: chipTone, label: chipLabel } = runStatusView(run);
  const statusChip = (
    <span className={statusPill(chipTone)}>
      <span className={statusDot(chipTone)} />
      {chipLabel}
    </span>
  );

  // Totals across all payslips for the hero card
  const totalNet = payslips.reduce((s, p) => s + Number(p.net_amount), 0);
  const totalAdditions = payslips.reduce((s, p) => s + Number(p.gross_additions), 0);
  const totalDeductions = payslips.reduce((s, p) => s + Number(p.gross_deductions), 0);

  return (
    <div>
      <PageHeader
        eyebrow={<><Link to="/dashboard/payroll" className="hover:text-foreground transition-colors">Payroll</Link> / {formatDate(run.period_start)}</>}
        title={`${formatDate(run.period_start)} – ${formatDate(run.period_end)}`}
        chip={statusChip}
        description={run.notes ?? 'A single payroll cycle. Add or adjust components, finalize, then distribute payslips.'}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/dashboard/payroll">
              <Button variant="ghost" type="button" className="rounded-full h-10 px-3 text-[13px] font-semibold gap-1.5 text-muted-foreground">
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            </Link>
            {payslips.length > 0 && (
              <Button
                type="button"
                variant="outline"
                disabled={exporting}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
                onClick={async () => {
                  setExporting(true);
                  try { await payrollApi.downloadRunCsv(run.id, run.period_start); }
                  catch (err: any) { toast.error('Export failed', { description: err.message }); }
                  finally { setExporting(false); }
                }}
              >
                <FileDown className="h-3.5 w-3.5" />
                {exporting ? 'Exporting…' : 'Export CSV'}
              </Button>
            )}
            {isCommitted && payslips.length > 0 && (
              <Button
                type="button"
                variant="outline"
                disabled={journalExporting}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
                onClick={async () => {
                  setJournalExporting(true);
                  try { await payrollApi.downloadRunJournal(run.id, run.period_start); }
                  catch (err: any) { toast.error('Journal export failed', { description: err.message }); }
                  finally { setJournalExporting(false); }
                }}
              >
                <FileDown className="h-3.5 w-3.5" />
                {journalExporting ? 'Exporting…' : 'Export to accounting'}
              </Button>
            )}
            {isCommitted && payslips.length > 0 && qbo.canPush && (
              <Button
                type="button"
                variant="outline"
                disabled={qboPushing}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
                onClick={async () => {
                  if (qboPushed && !(await confirmRePush(confirm, 'payroll run'))) return;
                  setQboPushing(true);
                  try {
                    const res = qboPushed
                      ? await quickbooksApi.pushPayrollRun(run.id, { force: true })
                      : await quickbooksApi.pushPayrollRun(run.id);
                    reportPushResult(res);
                    qbo.markPushed(run.id);
                  } catch (err: any) {
                    toast.error('Push to QuickBooks failed', { description: err.message });
                  } finally { setQboPushing(false); }
                }}
              >
                <PlugZap className="h-3.5 w-3.5" />
                {qboPushing ? 'Pushing…' : qboPushed ? 'Push again to QuickBooks' : 'Push to QuickBooks'}
              </Button>
            )}
            {isCommitted && payslips.length > 0 && (
              <Button
                type="button"
                variant="outline"
                disabled={achExporting}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
                onClick={() => exportAch(run.id, run.period_start)}
              >
                <Landmark className="h-3.5 w-3.5" />
                {achExporting ? 'Exporting…' : 'Export ACH'}
              </Button>
            )}
            {isDraft && (
              <>
                <Button disabled={busy !== null} variant="outline" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
                  onClick={() => runAction('Compute', () => payrollApi.computeRun(run.id),
                    'Recompute will replace auto components. Manual lines are preserved. Continue?')}>
                  <Calculator className="h-3.5 w-3.5" />
                  {busy === 'Compute' ? 'Computing…' : 'Compute'}
                </Button>
                <Button disabled={busy !== null} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5"
                  onClick={() => runAction('Finalize', () => payrollApi.finalizeRun(run.id),
                    'Finalize locks this run. You can revert only before distribution. Continue?')}>
                  {busy === 'Finalize' ? 'Finalizing…' : 'Finalize'}
                </Button>
              </>
            )}
            {canRevert && (
              <Button disabled={busy !== null} variant="outline" className="rounded-full h-10 px-4 text-[13px] font-semibold"
                onClick={() => runAction('Revert to draft', () => payrollApi.revertToDraft(run.id))}>
                {busy === 'Revert to draft' ? 'Reverting…' : 'Revert to draft'}
              </Button>
            )}
            {canDistribute && (
              <Button disabled={busy !== null} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5"
                onClick={() => runAction('Distribute', () => payrollApi.distributeRun(run.id),
                  'Email each employee their payslip? This action is irreversible.')}>
                <Send className="h-3.5 w-3.5" />
                {busy === 'Distribute' ? 'Distributing…' : 'Distribute'}
              </Button>
            )}
            {run.status === 'finalized' && (
              <Button
                type="button"
                disabled={busy !== null}
                className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5"
                onClick={() => markPaid(run.id)}
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                {busy === 'Mark as paid' ? 'Saving…' : 'Mark as paid'}
              </Button>
            )}
            {isCancellable && (
              <Button
                type="button"
                variant="ghost"
                disabled={busy !== null}
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5 text-muted-foreground hover:text-destructive"
                onClick={() => cancelRun(run.id)}
              >
                <Ban className="h-3.5 w-3.5" />
                {busy === 'Cancel' ? 'Cancelling…' : 'Cancel run'}
              </Button>
            )}
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6 max-w-5xl">
        {isPaid && (
          <div className="flex items-start gap-3 rounded-2xl px-5 py-4 bg-lime-neon/15 ring-1 ring-lime-neon/40 dark:bg-lime-neon/10 dark:ring-lime-neon/30">
            <Lock className="h-4 w-4 mt-0.5 shrink-0 text-ink/70 dark:text-lime-neon" strokeWidth={1.8} />
            <div className="min-w-0">
              <p className="text-[13.5px] font-semibold text-foreground">
                Locked — marked paid{run.paid_at ? ` on ${formatDate(run.paid_at)}` : ''}
              </p>
              <p className="text-[12.5px] text-muted-foreground mt-0.5">
                The money moved, so this run can no longer be reverted, cancelled, or edited. Exports and payslips stay available.
              </p>
            </div>
          </div>
        )}

        {/* Run totals hero */}
        {payslips.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 110, damping: 22 }}
            className={[VISUAL.bento, 'p-7 grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-6 items-center'].join(' ')}
          >
            <div>
              <p className={[VISUAL.eyebrow, 'mb-1'].join(' ')}>Total net</p>
              <p className={VISUAL.dashBigNumber}>{formatMoney(totalNet, currency)}</p>
              <p className="text-[12.5px] text-muted-foreground mt-2">
                {payslips.length} payslip{payslips.length === 1 ? '' : 's'} · {Object.keys(byEmp).length} employees
              </p>
            </div>
            <MiniKpi label="Additions" value={formatMoney(totalAdditions, currency)} tone="success" />
            <MiniKpi label="Deductions" value={formatMoney(totalDeductions, currency)} tone="muted" />
          </motion.section>
        )}

        <div className="space-y-3">
          {Object.entries(byEmp).map(([employeeId, comps]) => {
            const emp = employeesById[employeeId];
            const slip = slipsByEmp[employeeId];
            return (
              <EmployeeGroup
                key={employeeId}
                name={emp?.name ?? employeeId.slice(0, 8)}
                email={emp?.email ?? ''}
                comps={comps}
                slip={slip}
                currency={currency}
                isDraft={isDraft}
                busy={busy !== null}
                onRemove={(componentId) => runAction('Remove line',
                  () => payrollApi.removeComponent(run.id, componentId))}
              />
            );
          })}
        </div>

        {Object.keys(byEmp).length === 0 && (
          <div className={[VISUAL.bento, 'py-14 px-8 text-center'].join(' ')}>
            <p className="text-[14.5px] font-semibold text-foreground">No components yet</p>
            <p className="text-[13px] text-muted-foreground mt-1.5">
              {isDraft ? 'Click Compute above to derive them from HR attendance + penalty rules.' : 'Waiting on the next Compute.'}
            </p>
          </div>
        )}

        {isDraft && employeesById && Object.keys(employeesById).length > 0 && (
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setAddOpen(true)} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Add manual line
            </Button>
          </div>
        )}
      </div>

      <AddManualComponentDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        employees={empQ.data?.employees ?? []}
        runId={run.id}
        onAdded={() => { setAddOpen(false); reloadAll(); }}
      />
    </div>
  );
}

interface GroupProps {
  name: string;
  email: string;
  comps: PayrollComponent[];
  slip: Payslip | undefined;
  currency: string;
  isDraft: boolean;
  busy: boolean;
  onRemove: (componentId: string) => void;
}

function EmployeeGroup({ name, email, comps, slip, currency, isDraft, busy, onRemove }: GroupProps) {
  const [expanded, setExpanded] = React.useState(true);
  const additions = ['base', 'overtime', 'allowance', 'bonus'];
  const initial = (name?.[0] ?? '?').toUpperCase();

  return (
    <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors text-left"
      >
        <div className="h-11 w-11 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center text-[14px] font-bold text-saffron-foreground shrink-0">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">{name}</p>
          {email && <p className="text-[12px] text-muted-foreground truncate">{email}</p>}
        </div>
        {slip && (
          <div className="hidden sm:flex flex-col items-end gap-0.5">
            <span className="font-sans font-semibold text-[16px] tabular-nums tracking-[-0.02em] text-foreground">
              {formatMoney(slip.net_amount, currency)}
            </span>
            <span className="text-[11px] text-muted-foreground tabular-nums">
              <span className="text-emerald-600 dark:text-emerald-400">+{formatMoney(slip.gross_additions, currency)}</span>
              {' · '}
              <span className="text-rose-600 dark:text-rose-400">−{formatMoney(slip.gross_deductions, currency)}</span>
            </span>
          </div>
        )}
        {slip?.id && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              payrollApi.downloadPayslipPdf(slip.id).catch((err: any) =>
                toast.error('Download failed', { description: err?.message ?? String(err) }),
              );
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                payrollApi.downloadPayslipPdf(slip.id).catch((err: any) =>
                  toast.error('Download failed', { description: err?.message ?? String(err) }),
                );
              }
            }}
            className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-border/60 text-muted-foreground hover:text-foreground hover:ring-border/90 transition-colors cursor-pointer"
            aria-label="Download PDF payslip"
          >
            <FileDown className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className={['h-4 w-4 text-muted-foreground transition-transform shrink-0', expanded ? 'rotate-180' : ''].join(' ')} />
      </button>
      {expanded && (
        <div className="border-t border-border/40 bg-muted/20 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground/80">
                <th className="px-5 py-3 text-left font-semibold">Type</th>
                <th className="px-3 py-3 text-left font-semibold">Description</th>
                <th className="px-3 py-3 text-right font-semibold">Qty</th>
                <th className="px-3 py-3 text-right font-semibold">Amount</th>
                <th className="px-3 py-3 text-right font-semibold">Total</th>
                <th className="px-5" />
              </tr>
            </thead>
            <tbody>
              {comps.map((c) => {
                const sign = additions.includes(c.component_type) ? '+' : '−';
                const total = Number(c.amount) * Number(c.occurrences);
                return (
                  <tr key={c.id} className="border-t border-border/30">
                    <td className="px-5 py-2.5 align-top">
                      <span className="text-[11.5px] font-mono uppercase tracking-wide text-foreground/85">{c.component_type}</span>
                      {c.source === 'manual' && <Badge variant="secondary" className="ml-1.5 text-[10px] uppercase tracking-wide">manual</Badge>}
                    </td>
                    <td className="px-3 py-2.5 align-top text-[12.5px] text-foreground/90">{c.description}</td>
                    <td className="px-3 py-2.5 align-top text-right text-[12.5px] font-mono tabular-nums text-foreground/85">{c.occurrences}</td>
                    <td className="px-3 py-2.5 align-top text-right text-[12.5px] font-mono tabular-nums text-foreground/85">{formatMoney(c.amount, currency)}</td>
                    <td className="px-3 py-2.5 align-top text-right text-[13px] font-semibold font-mono tabular-nums text-foreground">{sign}{formatMoney(total, currency)}</td>
                    <td className="px-5 py-2.5 align-top text-right">
                      {isDraft && c.source === 'manual' && (
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onRemove(c.id)} className="rounded-full h-7 text-[11.5px]">
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ label, value, tone = 'neutral' }: { label: string; value: React.ReactNode; tone?: 'neutral' | 'success' | 'muted' }) {
  return (
    <div className={[
      'rounded-2xl ring-1 px-5 py-4',
      tone === 'success' ? 'bg-success/10 ring-success/25' :
      tone === 'muted'   ? 'bg-muted/40 ring-border/40' :
                           'bg-muted/40 ring-border/40',
    ].join(' ')}>
      <p className={[VISUAL.eyebrow, 'mb-1'].join(' ')}>{label}</p>
      <p className="font-sans font-semibold text-[20px] tabular-nums tracking-[-0.02em] text-foreground">{value}</p>
    </div>
  );
}
